import { Box, Button, Paper, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useState } from "react";
import { Task } from "../../../api/Tasks";
import { useTaskFeed } from "../../../misc/Feed";
import { PaletteGate } from "../../../misc/PaletteGate";
import { useDebounced } from "../../../misc/useDebounced";
import { useWorkspace } from "../../../misc/Workspace";
import { DayTaskRow } from "../day/DayTaskRow";
import { MobileTaskEditor } from "../day/MobileTaskEditor";
import { searchReady } from "../TaskPicker";
import { useCompleteTask } from "../useCompleteTask";

type Status = "open" | "done" | "all";

// resultsPage is how many more results "Load more" reads
const resultsPage = 25;

// minMobileSearch is the shortest word the phone search looks up. Typing is slow on a
// phone, so it starts sooner than the pickers do.
const minMobileSearch = 3;

// MobileSearchResults stands in for the page while a phone search is open. The page
// stays mounted under it, so closing the search shows the page as it was.
export function MobileSearchResults(props: { q: string }) {
    const ws = useWorkspace();
    const [status, setStatus] = useState<Status>("open");
    const [pages, setPages] = useState(1);
    const [open, setOpen] = useState<Task | null>(null);

    const term = useDebounced(props.q.trim(), 300);
    const ready = searchReady(term, minMobileSearch);

    const list = useTaskFeed(ready ? {
        search: term,
        team: ws.team || undefined,
        completed: status === "all" ? undefined : status === "done",
        limit: resultsPage * pages,
    } : null);

    const complete = useCompleteTask();

    return (
        <PaletteGate>
            <Box sx={{ pb: 4 }}>
                <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
                    <ToggleButtonGroup size="small" exclusive value={status}
                                       onChange={(_, value: Status | null) => {
                                           if (!value) return;
                                           setStatus(value);
                                           setPages(1);
                                       }}>
                        <ToggleButton value="open">Open</ToggleButton>
                        <ToggleButton value="done">Done</ToggleButton>
                        <ToggleButton value="all">All</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Box sx={{ px: 1 }}>{list.LoadingOrErrorElement}</Box>

                {!ready && (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ p: 4 }}>
                        Type a task number or 3 characters
                    </Typography>
                )}

                {ready && !list.loading && list.tasks.length === 0 && (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ p: 4 }}>
                        No matching tasks
                    </Typography>
                )}

                {list.tasks.length > 0 && (
                    <Paper variant="outlined" square sx={{ borderLeft: 0, borderRight: 0 }}>
                        {list.tasks.map(task => (
                            <DayTaskRow
                                key={task.id}
                                task={task}
                                showDue
                                showTeam={!ws.team}
                                disabled={complete.loading}
                                onClick={() => setOpen(task)}
                                onToggle={checked => complete.callback({ id: task.id, completed: checked })}
                            />
                        ))}
                    </Paper>
                )}

                {list.tasks.length < list.total && (
                    <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
                        <Button onClick={() => setPages(value => value + 1)}>Load more</Button>
                    </Box>
                )}
            </Box>

            {open && (
                <MobileTaskEditor
                    key={open.id}
                    value={open}
                    onClose={() => setOpen(null)}
                    onSaved={() => {}}
                />
            )}
        </PaletteGate>
    );
}
